import { Fragment } from "react";
import { useSelector, useDispatch } from "react-redux";
import moment from "moment";
import { Field, FieldArray, formValueSelector, change } from "redux-form";
import * as validations from "common/validations";
import { Panel, PanelBody } from "components/Panel";
import SelectPack from "components/SelectPack2";
import DatePickerInputPack from "components/MonthDayInputPack";
import ActionLink from "components/ActionLink";
import Alert from "components/Alert";
import { WriteMode } from "components/AnalysisRenderMode";

const MILKING_FREQUENCY_OPTIONS = [
    { value: "OnceADay", text: "Once a day" },
    { value: "TwiceADay", text: "Twice a day" },
    { value: "ThreeInTwo", text: "3 in 2" },
    { value: "TenInSeven", text: "10 in 7" },
];

const FREQUENCY_COLOUR = {
    OnceADay: "#f0c948",
    TwiceADay: "#7a5cc1",
    ThreeInTwo: "#f08c2e",
    TenInSeven: "#3a9bd5",
};

const FREQUENCY_LABEL = {
    OnceADay: "Once a day",
    TwiceADay: "Twice a day",
    ThreeInTwo: "3 in 2",
    TenInSeven: "10 in 7",
};

const FARM_MONTHS = ["Jul", "Aug", "Sep", "Oct", "Nov", "Dec", "Jan", "Feb", "Mar", "Apr", "May", "Jun"];

const parseDate = (s) => (s ? moment(s, "DD MMMM YYYY") : null);

// Parse a "DD MMMM" date into a farm-year-aware moment (Jul–Dec → 2000, Jan–Jun → 2001).
const parseFarmDate = (s) => {
    if (!s) return null;
    const d = moment(s, "DD MMMM");
    if (!d.isValid()) return null;
    d.year(d.month() >= 6 ? 2000 : 2001);
    return d;
};

// Convert a date to a 0..1 position along the farm year (Jul 1 → 0, Jun 30 → 1).
const toYearFraction = (date) => {
    if (!date || !date.isValid()) return null;
    const m = date.month();
    const d = date.date();
    const farmMonthIdx = m >= 6 ? m - 6 : m + 6; // 0..11
    const daysInMonth = date.daysInMonth();
    return (farmMonthIdx + (d - 1) / daysInMonth) / 12;
};

const toYearFractionMonthDay = (monthDay) => {
    if (!monthDay) return null;
    const parsed = moment(monthDay, "DD MMMM");
    return toYearFraction(parsed);
};

const getLactationLengthDays = (calvingDate, dryDate) => {
    const calving = parseFarmDate(calvingDate);
    const dry = parseFarmDate(dryDate);
    if (!calving || !dry) return null;
    const effectiveDry = dry.isBefore(calving) ? dry.clone().add(1, "year") : dry;
    return effectiveDry.diff(calving, "days");
};

const rangesOverlap = (a, b) => a.start < b.end && b.start < a.end;

// Returns a Set of field indices that have a validation error (overlap or under two weeks).
const findInvalidIndices = (periods) => {
    const invalid = new Set();

    const all = (periods || []).map((p, idx) => ({ p, idx }));

    // Flag rows under the two-week minimum
    for (const { p, idx } of all) {
        if (!p || !p.startDate || !p.endDate) {
            invalid.add(idx);
            continue;
        }
        const start = parseFarmDate(p.startDate);
        const end = parseFarmDate(p.endDate);
        if (start && end) {
            const effectiveEnd = end.isBefore(start) ? end.clone().add(1, "year") : end;
            if (effectiveEnd.diff(start, "days") < 14) {
                invalid.add(idx);
            }
        }
    }

    // Flag rows whose ranges overlap
    const candidates = all
        .map(({ p, idx }) => ({
            idx,
            start: toYearFraction(parseDate(p.startDate)),
            end: toYearFraction(parseDate(p.endDate)),
        }))
        .filter((r) => r.start !== null && r.end !== null);

    for (let i = 0; i < candidates.length; i++) {
        for (let j = i + 1; j < candidates.length; j++) {
            if (rangesOverlap(candidates[i], candidates[j])) {
                invalid.add(candidates[i].idx);
                invalid.add(candidates[j].idx);
            }
        }
    }
    return invalid;
};

const createValidatePeriods = (calvingDate, dryDate) => (periods) => {
    const all = periods || [];

    const calvingFrac = toYearFractionMonthDay(calvingDate);
    const dryFrac = toYearFractionMonthDay(dryDate);

    const rangeWraps = calvingFrac !== null && dryFrac !== null && calvingFrac > dryFrac;

    for (const p of all) {
        if (!p || !p.startDate || !p.endDate) {
            return "Start date and end date are required";
        }

        const start = parseFarmDate(p.startDate);
        const end = parseFarmDate(p.endDate);
        if (start && end) {
            const effectiveEnd = end.isBefore(start) ? end.clone().add(1, "year") : end;
            if (effectiveEnd.diff(start, "days") < 14) {
                return "Each period must be at least two weeks long";
            }
        }

        // Check period falls within calving–drying off range
        if (calvingFrac !== null && dryFrac !== null) {
            const startFrac = toYearFractionMonthDay(p.startDate);
            const endFrac = toYearFractionMonthDay(p.endDate);
            if (rangeWraps) {
                if (startFrac !== null && startFrac < calvingFrac && startFrac > dryFrac) {
                    return "Period dates must be within the calving to drying off range";
                }
                if (endFrac !== null && endFrac > dryFrac && endFrac < calvingFrac) {
                    return "Period dates must be within the calving to drying off range";
                }
            } else {
                if (startFrac !== null && startFrac < calvingFrac) {
                    return "Period dates must be within the calving to drying off range";
                }
                if (endFrac !== null && endFrac > dryFrac) {
                    return "Period dates must be within the calving to drying off range";
                }
            }
        }
    }

    const ranges = all
        .map((p) => ({
            start: toYearFraction(parseDate(p.startDate)),
            end: toYearFraction(parseDate(p.endDate)),
        }))
        .filter((r) => r.start !== null && r.end !== null);

    for (let i = 0; i < ranges.length; i++) {
        for (let j = i + 1; j < ranges.length; j++) {
            if (rangesOverlap(ranges[i], ranges[j])) {
                return "Date ranges must not overlap";
            }
        }
    }
    return undefined;
};

const FREQUENCY_COLOUR_TEXT = {
    OnceADay: "#fff",
    TwiceADay: "#fff",
    ThreeInTwo: "#fff",
};

const YearOverview = ({ periods, defaultFrequency, midCalvingDate, dryDate }) => {
    const hasSpecificPeriods = (periods || []).length > 0;

    const segments = [];

    // Add the default frequency as a full-year background segment
    segments.push({ frequency: defaultFrequency || "TwiceADay", isDefault: true, start: 0, end: 1 });

    // Add specific period segments on top
    (periods || []).forEach((p) => {
        const start = toYearFraction(parseDate(p.startDate));
        const end = toYearFraction(parseDate(p.endDate));
        if (start === null || end === null) return;
        const adjustedEnd = end === 0 ? 1 : end;
        segments.push({ ...p, isDefault: false, start, end: adjustedEnd });
    });

    const calving = toYearFractionMonthDay(midCalvingDate);
    const dry = toYearFractionMonthDay(dryDate);
    const lactationLengthDays = getLactationLengthDays(midCalvingDate, dryDate);

    // Unique frequencies actually used, in order of first appearance
    const usedFrequencies = segments.reduce((acc, s) => {
        if (s.frequency && !acc.includes(s.frequency)) acc.push(s.frequency);
        return acc;
    }, []);

    const events = [calving !== null && { pos: calving, label: "Calving" }, dry !== null && { pos: dry, label: "Drying off" }].filter(Boolean);

    return (
        <div className="u-mt-lg u-mb-lg">
            <div className="Field-label">Year overview — Jul to Jun</div>

            {/* Month tick labels */}
            <div style={{ display: "grid", gridTemplateColumns: "repeat(12, 1fr)", fontSize: 11, color: "#888", marginBottom: 4, marginTop: 4 }}>
                {FARM_MONTHS.map((m) => (
                    <div key={m} style={{ textAlign: "center" }}>
                        {m}
                    </div>
                ))}
            </div>

            {/* Main timeline bar */}
            <div style={{ position: "relative", height: 52, borderRadius: 6, background: "#e8e8e8", overflow: "hidden" }}>
                {segments.map((s, i) => {
                    const widthPct = Math.max(0, (s.end - s.start) * 100);
                    const isVisible = !s.isDefault || !hasSpecificPeriods;
                    return (
                        <div
                            key={i}
                            title={FREQUENCY_LABEL[s.frequency]}
                            style={{
                                position: "absolute",
                                left: `${s.start * 100}%`,
                                width: `${widthPct}%`,
                                top: 0,
                                bottom: 0,
                                background: FREQUENCY_COLOUR[s.frequency] || "#ccc",
                                opacity: s.isDefault && hasSpecificPeriods ? 0.25 : 1,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                overflow: "hidden",
                            }}
                        >
                            {isVisible && widthPct > 4 && (
                                <span
                                    style={{
                                        fontSize: 12,
                                        fontWeight: 600,
                                        color: FREQUENCY_COLOUR_TEXT[s.frequency] || "#fff",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        padding: "0 8px",
                                        textShadow: "0 1px 3px rgba(0,0,0,0.25)",
                                        maxWidth: "100%",
                                    }}
                                >
                                    {FREQUENCY_LABEL[s.frequency]}
                                </span>
                            )}
                        </div>
                    );
                })}

                {/* Non-lactation grey overlays — rendered on top of frequency segments */}
                {calving !== null && dry !== null && (
                    <>
                        {/* Pre-calving grey */}
                        {calving > 0 && <div style={{ position: "absolute", left: 0, width: `${calving * 100}%`, top: 0, bottom: 0, background: "rgba(220,220,220,0.72)", pointerEvents: "none" }} />}
                        {/* Post-drying-off grey */}
                        {dry < 1 && <div style={{ position: "absolute", left: `${dry * 100}%`, right: 0, top: 0, bottom: 0, background: "rgba(220,220,220,0.72)", pointerEvents: "none" }} />}
                        {/* Divider lines at calving and drying-off */}
                        <div style={{ position: "absolute", left: `${calving * 100}%`, top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,0.8)", pointerEvents: "none" }} />
                        <div style={{ position: "absolute", left: `${dry * 100}%`, top: 0, bottom: 0, width: 2, background: "rgba(255,255,255,0.8)", transform: "translateX(-2px)", pointerEvents: "none" }} />
                    </>
                )}
            </div>

            {/* Event markers */}
            {events.length > 0 && (
                <div style={{ position: "relative", height: 16, marginTop: 3 }}>
                    {events.map((ev, i) => (
                        <div
                            key={i}
                            style={{
                                position: "absolute",
                                left: `${ev.pos * 100}%`,
                                transform: "translateX(-50%)",
                                fontSize: 9,
                                fontWeight: 700,
                                letterSpacing: 0.8,
                                textTransform: "uppercase",
                                color: "#666",
                                whiteSpace: "nowrap",
                            }}
                        >
                            {ev.label}
                        </div>
                    ))}
                </div>
            )}

            {lactationLengthDays !== null && (
                <div style={{ fontSize: 12, color: "#555", textAlign: "center" }}>
                    <span className="Field-label" style={{ display: "inline", marginRight: 6 }}>
                        Lactation length
                    </span>
                    <span>
                        {lactationLengthDays} {lactationLengthDays === 1 ? "day" : "days"}
                    </span>
                </div>
            )}

            {/* Colour legend */}
            {usedFrequencies.length > 0 && (
                <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 20px", marginTop: events.length > 0 ? 4 : 8 }}>
                    {usedFrequencies.map((freq) => (
                        <div key={freq} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: "#555" }}>
                            <div style={{ width: 12, height: 12, borderRadius: 2, background: FREQUENCY_COLOUR[freq] || "#ccc", flexShrink: 0 }} />
                            {FREQUENCY_LABEL[freq]}
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

const FrequencyTable = ({ fields, meta, defaultFrequency, midCalvingDate, dryDate }) => {
    const overrideOptions = MILKING_FREQUENCY_OPTIONS.filter((o) => o.value !== defaultFrequency);
    const defaultNewFrequency = overrideOptions.length > 0 ? overrideOptions[0].value : "TwiceADay";

    const _add = () => {
        const allPeriods = fields.getAll() || [];
        let latestEnd = null;
        for (const p of allPeriods) {
            if (p.endDate) {
                const d = parseFarmDate(p.endDate);
                if (d && (!latestEnd || d.isAfter(latestEnd))) latestEnd = d;
            }
        }
        const startDate = latestEnd ? latestEnd.clone().add(1, "day").format("DD MMMM") : midCalvingDate || "01 December";
        fields.push({ frequency: defaultNewFrequency, startDate, endDate: dryDate || "30 December" });
    };
    const _remove = (e, index) => {
        e.preventDefault();
        fields.remove(index);
    };

    const allPeriods = fields.getAll() || [];
    const overlappingIndices = findInvalidIndices(allPeriods);

    return (
        <Fragment>
            <div className="Table u-mt-md" style={{ overflow: "visible" }}>
                <table>
                    <thead>
                        <tr>
                            <th data-width="md-25">Milking frequency</th>
                            <th data-width="md-25">Start date</th>
                            <th data-width="md-25">End date</th>
                            <WriteMode>
                                <th className="th--shrink">&nbsp;</th>
                            </WriteMode>
                        </tr>
                    </thead>
                    <tbody>
                        {fields.length === 0 && (
                            <tr>
                                <td colSpan="4">
                                    <div className="u-flex u-flexAlignItemsCenter">
                                        <i className="icon icon-info u-textSecondary u-mr-sm" />
                                        <span>No milking frequency periods have been added</span>
                                    </div>
                                </td>
                            </tr>
                        )}
                        {fields.map((name, index) => {
                            const hasOverlap = overlappingIndices.has(index);
                            return (
                                <tr key={index} style={hasOverlap ? { background: "#fff3f3" } : undefined}>
                                    <td>
                                        <Field name={`${name}.frequency`} options={overrideOptions} component={SelectPack} noLabel />
                                    </td>
                                    <td>
                                        <Field name={`${name}.startDate`} component={DatePickerInputPack} noLabel placeholder="Select date" validate={[validations.required]} />
                                    </td>
                                    <td>
                                        <Field name={`${name}.endDate`} component={DatePickerInputPack} noLabel placeholder="Select date" validate={[validations.required]} />
                                    </td>
                                    <WriteMode>
                                        <td className="u-textCenter">
                                            <ActionLink className="IconLink--trash" onClick={(e) => _remove(e, index)} />
                                        </td>
                                    </WriteMode>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {meta.error && <Alert type="error" text={meta.error} className="u-mt-sm" />}
            <WriteMode>
                <div className="u-mt-md">
                    <div className="u-flex u-flexJustifyEnd">
                        <ul className="ActionsBar-links">
                            <li>
                                <span id="add-period-link" onClick={_add} className="IconLink--arrow-plus a u-link">
                                    Add period
                                </span>
                            </li>
                        </ul>
                    </div>
                </div>
            </WriteMode>
        </Fragment>
    );
};

export default function MilkingFrequencySection() {
    const dispatch = useDispatch();
    const selector = formValueSelector("modal-form");
    const periods = useSelector((state) => selector(state, "milkingFrequencies")) || [];
    const defaultFrequency = useSelector((state) => selector(state, "defaultMilkingFrequency")) || "TwiceADay";
    const midCalvingDate = useSelector((state) => selector(state, "calvingDetails.midCalvingDate"));
    const dryDate = useSelector((state) => selector(state, "calvingDetails.dryDate"));

    const onDefaultFrequencyChanged = (e, newValue) => {
        const filtered = periods.filter((p) => p.frequency !== newValue);
        if (filtered.length !== periods.length) {
            dispatch(change("modal-form", "milkingFrequencies", filtered));
        }
    };

    return (
        <div className="Grid-cell u-mt-lg">
            <Panel title="Milking frequency schedule" midBlue open={periods.length > 0}>
                <PanelBody>
                    <Field name="defaultMilkingFrequency" label="Default milking frequency" options={MILKING_FREQUENCY_OPTIONS} dataWidth="50" component={SelectPack} onChange={onDefaultFrequencyChanged} />
                    <YearOverview periods={periods} defaultFrequency={defaultFrequency} midCalvingDate={midCalvingDate} dryDate={dryDate} />
                    <FieldArray name="milkingFrequencies" component={FrequencyTable} defaultFrequency={defaultFrequency} midCalvingDate={midCalvingDate} dryDate={dryDate} validate={[createValidatePeriods(midCalvingDate, dryDate)]} />
                </PanelBody>
            </Panel>
        </div>
    );
}
